import React, { useState, useEffect, useRef } from 'react';
import { obtenerDatosFinancieros, obtenerResumenFinanciero, registrarEgresoFinanciero, editarVenta, eliminarVenta } from '../api/backend_v2';
import { formatearFechaArgentina } from '../utils/fechas';
import { formatearMoneda } from '../utils/moneda';
import Swal from 'sweetalert2';
import {
  ChartBarIcon,
  BriefcaseIcon,
  BanknotesIcon,
  CurrencyDollarIcon,
  BuildingLibraryIcon,
  MinusCircleIcon,
  CalendarIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  PrinterIcon,
  TagIcon,
  EyeIcon,
  ArrowTrendingDownIcon,
  PencilIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import * as XLSX from 'xlsx';
import CalendarioPagos from './CalendarioPagos';
import GestionEgresos from '../components/GestionEgresos';

const Finanzas = () => {
  const [registros, setRegistros] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [paginaActual, setPaginaActual] = useState(1);
  const REGISTROS_POR_PAGINA = 10;
  const [filtros, setFiltros] = useState({
    mes: '',
    anio: new Date().getFullYear().toString(),
    fechaInicio: '',
    fechaFin: '',
    limite: REGISTROS_POR_PAGINA,
    offset: 0
  });
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [procesandoAccion, setProcesandoAccion] = useState(false);
  const [mostrarModalEgreso, setMostrarModalEgreso] = useState(false);
  const [mostrarCalendarioPagos, setMostrarCalendarioPagos] = useState(false);
  const [mostrarGestionEgresos, setMostrarGestionEgresos] = useState(false);
  const [mostrarModalEditarVenta, setMostrarModalEditarVenta] = useState(false);
  const [ventaParaEditar, setVentaParaEditar] = useState(null);
  const [procesandoVenta, setProcesandoVenta] = useState(false);
  const [formEgreso, setFormEgreso] = useState({
    fecha: new Date().toISOString().slice(0, 10),
    importe: '',
    tipo: 'efectivo',
    receptor: '',
    fecha_cobro: '',
    nro_cheque: '',
  });

  // Referencias para sincronizar scroll de la barra superior
  const scrollBarRef = useRef(null);
  const tableContainerRef = useRef(null);

  useEffect(() => {
    cargarDatos(filtros);
  }, []);

  // Sincronizar scroll de la barra superior con la tabla
  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    const scrollBar = scrollBarRef.current;

    if (!tableContainer || !scrollBar) return;

    const handleTableScroll = () => {
      // Calcular el porcentaje de scroll
      const scrollWidth = tableContainer.scrollWidth - tableContainer.clientWidth;
      if (scrollWidth > 0) {
        const scrollPercentage = (tableContainer.scrollLeft / scrollWidth) * 100;
        
        // Sincronizar la posición de scroll de la barra
        scrollBar.scrollLeft = tableContainer.scrollLeft;
        
        // Actualizar el ancho y posición de la barra de progreso
        const progressBar = scrollBar.querySelector('div:last-child');
        if (progressBar?.style) {
          progressBar.style.width = `${Math.max(15, 100 - scrollPercentage * 0.5)}%`;
          progressBar.style.marginLeft = `${scrollPercentage * 0.5}%`;
        }
      }
    };

    const handleScrollBarScroll = () => {
      tableContainer.scrollLeft = scrollBar.scrollLeft;
    };

    tableContainer.addEventListener('scroll', handleTableScroll);
    scrollBar.addEventListener('scroll', handleScrollBarScroll);

    // Inicializar la posición
    handleTableScroll();

    return () => {
      tableContainer.removeEventListener('scroll', handleTableScroll);
      scrollBar.removeEventListener('scroll', handleScrollBarScroll);
    };
  }, [registros]); // Re-bind cuando cambian los registros

  const cargarDatos = async (filtrosActuales = filtros) => {
    try {
      setLoading(true);
      const [datosFinancieros, resumenData] = await Promise.all([
        obtenerDatosFinancieros(filtrosActuales),
        obtenerResumenFinanciero()
      ]);
      setRegistros(datosFinancieros.registros || []);
      setTotalRegistros(datosFinancieros.total || 0);
      setResumen(resumenData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const cambiarPagina = async (nuevaPagina) => {
    const offset = (nuevaPagina - 1) * REGISTROS_POR_PAGINA;
    const nuevosFiltros = { ...filtros, offset };
    setFiltros(nuevosFiltros);
    setPaginaActual(nuevaPagina);
    await cargarDatos(nuevosFiltros);
  };

  const totalPaginas = Math.ceil(totalRegistros / REGISTROS_POR_PAGINA);

  const paginasDisponibles = () => {
    const paginas = [];
    const maxPaginas = 5;
    let inicio = Math.max(1, paginaActual - Math.floor(maxPaginas / 2));
    let fin = Math.min(totalPaginas, inicio + maxPaginas - 1);
    
    if (fin - inicio < maxPaginas - 1) {
      inicio = Math.max(1, fin - maxPaginas + 1);
    }

    for (let i = inicio; i <= fin; i++) {
      paginas.push(i);
    }
    return paginas;
  };

  const aplicarFiltros = async () => {
    const filtrosConPaginacion = { ...filtros, offset: 0 };
    setFiltros(filtrosConPaginacion);
    setPaginaActual(1);
    await cargarDatos(filtrosConPaginacion);
  };

  const limpiarFiltros = async () => {
    const filtrosLimpios = {
      mes: '',
      anio: new Date().getFullYear().toString(),
      fechaInicio: '',
      fechaFin: '',
      limite: REGISTROS_POR_PAGINA,
      offset: 0
    };
    setFiltros(filtrosLimpios);
    setPaginaActual(1);
    await cargarDatos(filtrosLimpios);
  };

  const aplicarMesEnCurso = async () => {
    const ahora = new Date();
    const nuevosFiltros = {
      ...filtros,
      mes: String(ahora.getMonth() + 1),
      anio: String(ahora.getFullYear()),
      fechaInicio: '',
      fechaFin: '',
      offset: 0,
    };
    setFiltros(nuevosFiltros);
    setPaginaActual(1);
    await cargarDatos(nuevosFiltros);
  };

  const abrirModalEgreso = () => {
    setError(null);
    setFormEgreso({
      fecha: new Date().toISOString().slice(0, 10),
      importe: '',
      tipo: 'efectivo',
      receptor: '',
      fecha_cobro: '',
      nro_cheque: '',
    });
    setMostrarModalEgreso(true);
  };

  const eliminarVentaConfirm = (venta) => {
    Swal.fire({
      title: '¿Eliminar venta?',
      html: `<p>¿Estás seguro que querés eliminar esta venta? Esta acción no se puede deshacer.</p><p class="text-sm text-gray-600 mt-2">Venta: ${venta.id_registro} | Cliente: ${venta.cliente?.nombre} ${venta.cliente?.apellido} | Monto: $${venta.precio_total?.toLocaleString()}</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setProcesandoVenta(true);
          await eliminarVenta(venta.id_registro);
          Swal.fire('Eliminada', 'La venta fue eliminada exitosamente.', 'success');
          await cargarDatos(filtros);
        } catch (err) {
          Swal.fire('Error', err.message || 'No se pudo eliminar la venta.', 'error');
          console.error('Error al eliminar venta:', err);
        } finally {
          setProcesandoVenta(false);
        }
      }
    });
  };

  const guardarEdicionVenta = async (e) => {
    e.preventDefault();
    
    if (!ventaParaEditar || procesandoVenta) return;

    try {
      setProcesandoVenta(true);
      const datosActualizados = {
        tipo_venta_id: ventaParaEditar.tipo_venta_id,
        tipo_venta_nombre: ventaParaEditar.tipo_venta_nombre,
        cliente_id: ventaParaEditar.cliente_id,
        cliente_nombre: ventaParaEditar.cliente?.nombre,
        descripcion: ventaParaEditar.observaciones || '',
        monto: ventaParaEditar.precio_total,
        tipo_pago: ventaParaEditar.tipo_pago,
        fecha_venta: ventaParaEditar.fecha_pedido,
        id_anteojo: ventaParaEditar.id_anteojo || null,
        id_producto: ventaParaEditar.id_producto || null,
      };

      await editarVenta(ventaParaEditar.id_registro, datosActualizados);
      Swal.fire('Éxito', 'Venta editada exitosamente.', 'success');
      setMostrarModalEditarVenta(false);
      setVentaParaEditar(null);
      await cargarDatos(filtros);
    } catch (err) {
      Swal.fire('Error', err.message || 'No se pudo editar la venta.', 'error');
      console.error('Error al editar venta:', err);
    } finally {
      setProcesandoVenta(false);
    }
  };

  const cerrarModalEgreso = () => {
    if (procesandoAccion) return;
    setMostrarModalEgreso(false);
  };

  const actualizarFormEgreso = (campo, valor) => {
    setFormEgreso((prev) => {
      const next = { ...prev, [campo]: valor };
      if (campo === 'tipo') {
        if (valor !== 'cheque') {
          next.nro_cheque = '';
        }
        if (valor !== 'cheque' && valor !== 'transferencia' && valor !== 'a_pagar_efet') {
          next.fecha_cobro = '';
        }
      }
      return next;
    });
  };

  const guardarEgreso = async (e) => {
    e.preventDefault();

    const importeNumerico = Number(formEgreso.importe);
    if (!formEgreso.fecha || !formEgreso.tipo || !formEgreso.receptor.trim() || !Number.isFinite(importeNumerico) || importeNumerico <= 0) {
      setError('Completá fecha, tipo, receptor e importe mayor a 0.');
      return;
    }

    if (['cheque', 'transferencia', 'a_pagar_efet'].includes(formEgreso.tipo) && !formEgreso.fecha_cobro) {
      setError('Para cheque, transferencia y A pagar (efect.) debés ingresar fecha de cobro.');
      return;
    }

    if (formEgreso.tipo === 'cheque' && !formEgreso.nro_cheque.trim()) {
      setError('Para tipo cheque debés ingresar número de cheque.');
      return;
    }

    try {
      setProcesandoAccion(true);
      setError(null);

      const esProgramado = ['cheque', 'transferencia', 'a_pagar_efet'].includes(formEgreso.tipo);
      const payload = {
        fecha: formEgreso.fecha,
        importe: importeNumerico,
        tipo: formEgreso.tipo,
        receptor: formEgreso.receptor.trim(),
        fecha_cobro: esProgramado ? formEgreso.fecha_cobro : null,
        nro_cheque: formEgreso.tipo === 'cheque' ? formEgreso.nro_cheque.trim() : null,
        pagado: !esProgramado ? true : undefined,
      };

      await registrarEgresoFinanciero(payload);
      setMostrarModalEgreso(false);
      await cargarDatos(filtros);
    } catch (err) {
      setError(err.message || 'No se pudo registrar el egreso.');
    } finally {
      setProcesandoAccion(false);
    }
  };

  const obtenerRegistrosParaAccion = async (usarFiltros) => {
    const base = usarFiltros
      ? {
          fechaInicio: filtros.fechaInicio,
          fechaFin: filtros.fechaFin,
          mes: filtros.mes,
          anio: filtros.anio,
        }
      : {};

    const limiteConsulta = 500;
    let offset = 0;
    let acumulados = [];

    while (true) {
      const response = await obtenerDatosFinancieros({
        ...base,
        limite: limiteConsulta,
        offset,
      });
      const chunk = response?.registros || [];
      acumulados = acumulados.concat(chunk);

      if (chunk.length < limiteConsulta) break;
      offset += limiteConsulta;

      // Tope de seguridad para evitar loops infinitos.
      if (offset > 50000) break;
    }

    return acumulados;
  };

  const obtenerNumeroCaso = (registro) => {
    const nroSobre = registro?.nro_sobre;
    return nroSobre !== null && nroSobre !== undefined && nroSobre !== '' ? nroSobre : '-';
  };

  const calcularTotales = (data) => ({
    totalGeneral: data.reduce((sum, r) => sum + (Number(r.precio_total) || 0), 0),
    totalSenas: data.reduce((sum, r) => sum + (Number(r.sena) || 0), 0),
    totalSaldos: data.reduce((sum, r) => sum + (Number(r.saldo) || 0), 0),
  });

  const convertirRegistrosAExcel = (data) =>
    data.map((registro) => ({
      Origen: registro.tipo_origen === 'venta' ? 'Venta directa' : 'Anteojo',
      Sobre: obtenerNumeroCaso(registro),
      Cliente: `${registro.cliente?.nombre || ''} ${registro.cliente?.apellido || ''}`.trim(),
      Telefono: registro.cliente?.telefono || '',
      Fecha: formatearFecha(registro.fecha_pedido),
      Tipo: registro.tipo_anteojo || '',
      Detalles: registro.tipo_origen === 'venta'
        ? (registro.observaciones || '')
        : `${registro.cristal || ''} / ${registro.armazon || ''}`,
      FormaPago: registro.tipo_pago || '',
      Total: registro.precio_total || 0,
      Sena: registro.sena || 0,
      Saldo: registro.saldo || 0,
      Estado: registro.estado || '',
    }));

  const exportarExcel = async (usarFiltros) => {
    try {
      setProcesandoAccion(true);
      const data = await obtenerRegistrosParaAccion(usarFiltros);
      if (data.length === 0) {
        setError('No hay registros para exportar.');
        return;
      }

      const totales = calcularTotales(data);
      const rows = [
        ...convertirRegistrosAExcel(data),
        {},
        { Sobre: 'TOTAL GENERAL', Total: totales.totalGeneral },
        { Sobre: 'TOTAL SENAS', Sena: totales.totalSenas },
        { Sobre: 'TOTAL SALDOS', Saldo: totales.totalSaldos },
      ];
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Finanzas');

      const fechaArchivo = new Date().toISOString().slice(0, 10);
      const scope = usarFiltros ? 'filtrado' : 'completo';
      XLSX.writeFile(wb, `finanzas_${scope}_${fechaArchivo}.xlsx`);
    } catch (err) {
      setError(err.message || 'Error al exportar Excel');
    } finally {
      setProcesandoAccion(false);
    }
  };

  const imprimirRegistros = async (usarFiltros) => {
    try {
      setProcesandoAccion(true);
      const data = await obtenerRegistrosParaAccion(usarFiltros);
      if (data.length === 0) {
        setError('No hay registros para imprimir.');
        return;
      }

      const titulo = usarFiltros ? 'Reporte Financiero (Filtrado)' : 'Reporte Financiero (Completo)';
      const fechaImpresion = new Date().toLocaleString('es-AR');
      const totales = calcularTotales(data);

      const filas = data
        .map(
          (r) => `
            <tr>
              <td>${r.tipo_origen === 'venta' ? 'Venta' : 'Anteojo'}</td>
              <td>#${obtenerNumeroCaso(r)}</td>
              <td>${(r.cliente?.nombre || '')} ${(r.cliente?.apellido || '')}</td>
              <td>${formatearFecha(r.fecha_pedido)}</td>
              <td>${r.tipo_anteojo || ''}</td>
              <td>${r.tipo_origen === 'venta' ? (r.observaciones || '-') : `${r.cristal || ''} / ${r.armazon || ''}`}</td>
              <td>${r.tipo_pago || '-'}</td>
              <td>${formatearMoneda(r.precio_total || 0)}</td>
              <td>${formatearMoneda(r.sena || 0)}</td>
              <td>${formatearMoneda(r.saldo || 0)}</td>
              <td>${r.estado || ''}</td>
            </tr>
          `
        )
        .join('');

      const win = window.open('', '_blank', 'width=1200,height=800');
      if (!win) {
        setError('El navegador bloqueó la ventana de impresión.');
        return;
      }

      win.document.write(`
        <html>
          <head>
            <title>${titulo}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 24px; color: #111827; }
              h1 { margin: 0 0 4px 0; font-size: 20px; }
              p { margin: 0 0 16px 0; color: #4b5563; }
              table { width: 100%; border-collapse: collapse; font-size: 12px; }
              th, td { border: 1px solid #d1d5db; padding: 8px; text-align: left; }
              th { background: #f3f4f6; }
            </style>
          </head>
          <body>
            <h1>${titulo}</h1>
            <p>Generado: ${fechaImpresion}</p>
            <table>
              <thead>
                <tr>
                  <th>Origen</th>
                  <th>Sobre</th>
                  <th>Cliente</th>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Detalles</th>
                  <th>Forma de Pago</th>
                  <th>Total</th>
                  <th>Seña</th>
                  <th>Saldo</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                ${filas}
              </tbody>
            </table>
            <p style="margin-top: 14px; color: #111827;">
              <strong>Total General:</strong> ${formatearMoneda(totales.totalGeneral)}
              &nbsp;&nbsp;|&nbsp;&nbsp;
              <strong>Total Señas:</strong> ${formatearMoneda(totales.totalSenas)}
              &nbsp;&nbsp;|&nbsp;&nbsp;
              <strong>Total Saldos:</strong> ${formatearMoneda(totales.totalSaldos)}
            </p>
            <script>
              window.onload = function () {
                window.print();
                window.onafterprint = function () { window.close(); };
              };
            </script>
          </body>
        </html>
      `);
      win.document.close();
    } catch (err) {
      setError(err.message || 'Error al imprimir');
    } finally {
      setProcesandoAccion(false);
    }
  };

  const formatearFecha = formatearFechaArgentina;

  // usar formatearMoneda compartida (sin centavos)

  const meses = [
    { value: '1', label: 'Enero' },
    { value: '2', label: 'Febrero' },
    { value: '3', label: 'Marzo' },
    { value: '4', label: 'Abril' },
    { value: '5', label: 'Mayo' },
    { value: '6', label: 'Junio' },
    { value: '7', label: 'Julio' },
    { value: '8', label: 'Agosto' },
    { value: '9', label: 'Septiembre' },
    { value: '10', label: 'Octubre' },
    { value: '11', label: 'Noviembre' },
    { value: '12', label: 'Diciembre' }
  ];

  const anioActual = new Date().getFullYear();
  const anios = Array.from({ length: 6 }, (_, i) => String(anioActual - i));

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="p-2 md:p-4 lg:p-6 bg-gray-50 min-h-screen">
      <div className="mx-auto" style={{ maxWidth: '99%' }}>
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <BanknotesIcon className="w-8 h-8 text-green-600" />
              Finanzas
            </h1>
            <p className="text-gray-600 mt-2">Control financiero y reportes de la óptica</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={abrirModalEgreso}
              disabled={procesandoAccion}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50"
            >
              <MinusCircleIcon className="w-4 h-4 mr-1.5" />
              Registrar Egreso / Gasto
            </button>
            <button
              onClick={() => setMostrarCalendarioPagos(true)}
              disabled={procesandoAccion}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              <CalendarIcon className="w-4 h-4 mr-1.5" />
              Calendario de Pagos
            </button>
            <button
              onClick={() => setMostrarGestionEgresos(true)}
              disabled={procesandoAccion}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-700 hover:bg-red-800 disabled:opacity-50"
            >
              <ArrowTrendingDownIcon className="w-4 h-4 mr-1.5" />
              Gestionar Egresos
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            <strong>Error:</strong> {error}
            <button 
              onClick={() => setError(null)}
              className="ml-4 text-red-500 hover:text-red-700"
            >
              ✕
            </button>
          </div>
        )}

        {/* Resumen Financiero */}
        {resumen && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                      <BriefcaseIcon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Total Trabajos
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {resumen.totales?.trabajos || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                      <BanknotesIcon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Ingresos Totales
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {formatearMoneda(resumen.totales?.ingresos_totales || 0)}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                      <CurrencyDollarIcon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Total Señas
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {formatearMoneda(resumen.totales?.total_senas || 0)}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-red-500 rounded-md flex items-center justify-center">
                      <BuildingLibraryIcon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Saldos Pendientes
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {formatearMoneda(resumen.totales?.saldos_pendientes || 0)}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filtros */}
        <div className="bg-white shadow rounded-lg p-2 md:p-4 lg:p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Filtros y Controles</h3>
            <button
              onClick={() => setMostrarFiltros(!mostrarFiltros)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {mostrarFiltros ? <ChevronDownIcon className="w-4 h-4 mr-1" /> : <ChevronRightIcon className="w-4 h-4 mr-1" />}
              {mostrarFiltros ? 'Ocultar' : 'Mostrar'} Filtros
            </button>
          </div>

          {mostrarFiltros && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-4">
              <div>
                <label htmlFor="filtro-mes" className="block text-sm font-medium text-gray-700 mb-1">Mes</label>
                <select
                  id="filtro-mes"
                  value={filtros.mes}
                  onChange={(e) => setFiltros({...filtros, mes: e.target.value})}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">Todos los meses</option>
                  {meses.map(mes => (
                    <option key={mes.value} value={mes.value}>{mes.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="filtro-anio" className="block text-sm font-medium text-gray-700 mb-1">Año</label>
                <select
                  id="filtro-anio"
                  value={filtros.anio}
                  onChange={(e) => setFiltros({...filtros, anio: e.target.value})}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  {anios.map((anio) => (
                    <option key={anio} value={anio}>{anio}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="filtro-fecha-inicio" className="block text-sm font-medium text-gray-700 mb-1">Fecha Inicio</label>
                <input
                  id="filtro-fecha-inicio"
                  type="date"
                  value={filtros.fechaInicio}
                  onChange={(e) => setFiltros({ ...filtros, fechaInicio: e.target.value, mes: '' })}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="filtro-fecha-fin" className="block text-sm font-medium text-gray-700 mb-1">Fecha Fin</label>
                <input
                  id="filtro-fecha-fin"
                  type="date"
                  value={filtros.fechaFin}
                  onChange={(e) => setFiltros({ ...filtros, fechaFin: e.target.value, mes: '' })}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="filtro-limite" className="block text-sm font-medium text-gray-700 mb-1">Registros por página</label>
                <select
                  id="filtro-limite"
                  value={filtros.limite}
                  onChange={(e) => setFiltros({...filtros, limite: parseInt(e.target.value)})}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="5">5 registros</option>
                  <option value="10">10 registros</option>
                  <option value="25">25 registros</option>
                  <option value="50">50 registros</option>
                  <option value="100">100 registros</option>
                  <option value="200">200 registros</option>
                </select>
              </div>

              <div className="flex items-end space-x-2">
                <button
                  onClick={aplicarFiltros}
                  disabled={procesandoAccion}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <MagnifyingGlassIcon className="w-4 h-4 mr-1.5" />
                  Aplicar
                </button>
                <button
                  onClick={limpiarFiltros}
                  disabled={procesandoAccion}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <TrashIcon className="w-4 h-4 mr-1.5" />
                  Limpiar
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2.5 mt-2 mb-4">
            <button
              onClick={aplicarMesEnCurso}
              disabled={procesandoAccion}
              className="inline-flex items-center justify-center px-3 py-2 border border-indigo-200 text-xs sm:text-sm font-semibold rounded-xl text-indigo-700 bg-indigo-50/80 hover:bg-indigo-100 transition-colors shadow-2xs disabled:opacity-50"
            >
              <CalendarDaysIcon className="w-4 h-4 mr-1.5" />
              Mes en curso
            </button>
            <button
              onClick={() => exportarExcel(true)}
              disabled={procesandoAccion}
              className="inline-flex items-center justify-center px-3 py-2 border border-emerald-200 text-xs sm:text-sm font-semibold rounded-xl text-emerald-700 bg-emerald-50/80 hover:bg-emerald-100 transition-colors shadow-2xs disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-1.5" />
              Excel filtrado
            </button>
            <button
              onClick={() => exportarExcel(false)}
              disabled={procesandoAccion}
              className="inline-flex items-center justify-center px-3 py-2 border border-emerald-200 text-xs sm:text-sm font-semibold rounded-xl text-emerald-700 bg-emerald-50/80 hover:bg-emerald-100 transition-colors shadow-2xs disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-1.5" />
              Excel completo
            </button>
            <button
              onClick={() => imprimirRegistros(true)}
              disabled={procesandoAccion}
              className="inline-flex items-center justify-center px-3 py-2 border border-slate-200 text-xs sm:text-sm font-semibold rounded-xl text-slate-700 bg-slate-50 hover:bg-slate-100 transition-colors shadow-2xs disabled:opacity-50"
            >
              <PrinterIcon className="w-4 h-4 mr-1.5" />
              Imprimir filtrado
            </button>
            <button
              onClick={() => imprimirRegistros(false)}
              disabled={procesandoAccion}
              className="inline-flex items-center justify-center px-3 py-2 border border-slate-200 text-xs sm:text-sm font-semibold rounded-xl text-slate-700 bg-slate-50 hover:bg-slate-100 transition-colors shadow-2xs disabled:opacity-50"
            >
              <PrinterIcon className="w-4 h-4 mr-1.5" />
              Imprimir completo
            </button>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="text-sm text-gray-600">
              Mostrando {registros.length} de {totalRegistros} registros {procesandoAccion ? '· Procesando...' : ''}
            </div>
            
            {/* Controles de Paginación */}
            {totalPaginas > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => cambiarPagina(Math.max(1, paginaActual - 1))}
                  disabled={paginaActual === 1 || loading}
                  className="px-2 py-1 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ← Anterior
                </button>
                
                <div className="flex gap-1">
                  {paginasDisponibles().map((pagina) => (
                    <button
                      key={pagina}
                      onClick={() => cambiarPagina(pagina)}
                      disabled={loading}
                      className={`px-2.5 py-1 rounded text-sm font-medium ${
                        pagina === paginaActual
                          ? 'bg-green-600 text-white'
                          : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {pagina}
                    </button>
                  ))}
                </div>
                
                <button
                  onClick={() => cambiarPagina(Math.min(totalPaginas, paginaActual + 1))}
                  disabled={paginaActual === totalPaginas || loading}
                  className="px-2 py-1 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Siguiente →
                </button>
                
                <span className="text-sm text-gray-600 ml-2">
                  Página {paginaActual} de {totalPaginas}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Tabla de Registros Financieros */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-2 md:px-4 lg:px-6 py-4 md:py-5 sm:px-4 lg:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Detalle de Trabajos Realizados
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Información detallada de cada trabajo con datos financieros
            </p>
          </div>
          
          {registros.length === 0 ? (
            <div className="text-center py-12">
              <ChartBarIcon className="h-16 w-16 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No hay registros</h3>
              <p className="mt-1 text-sm text-gray-500">
                No se encontraron trabajos con los filtros aplicados
              </p>
            </div>
          ) : (
            <>

              {/* Tabla con sincronización de scroll */}
              <div 
                ref={tableContainerRef}
                className="overflow-x-auto shadow-sm border border-gray-200 rounded-b-lg"
                style={{ scrollBehavior: 'smooth' }}
              >
                <div className="min-w-full">
                  <table className="min-w-full divide-y divide-gray-200 text-xs md:text-sm">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                    <th className="px-2 md:px-3 lg:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Origen
                    </th>
                    <th className="px-2 md:px-3 lg:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Sobre
                    </th>
                    <th className="px-2 md:px-3 lg:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Cliente
                    </th>
                    <th className="px-2 md:px-3 lg:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap hidden md:table-cell">
                      Fecha
                    </th>
                    <th className="px-2 md:px-3 lg:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap hidden lg:table-cell">
                      Tipo
                    </th>
                    <th className="px-2 md:px-3 lg:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Detalles
                    </th>
                    <th className="px-2 md:px-3 lg:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap hidden xl:table-cell">
                      Pago
                    </th>
                    <th className="px-2 md:px-3 lg:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Total
                    </th>
                    <th className="px-2 md:px-3 lg:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap hidden sm:table-cell">
                      Seña
                    </th>
                    <th className="px-2 md:px-3 lg:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Saldo
                    </th>
                    <th className="px-2 md:px-3 lg:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap hidden md:table-cell">
                      Estado
                    </th>
                    <th className="px-2 md:px-3 lg:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Acc.
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {registros.map((registro, index) => (
                    <tr key={`${registro.tipo_origen || 'a'}-${registro.id_registro || 'sin-id'}-${index}`} className="hover:bg-gray-50">
                      <td className="px-2 md:px-3 lg:px-4 py-2 md:py-3 whitespace-nowrap">
                        {registro.tipo_origen === 'venta' ? (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800"><TagIcon className="w-2.5 h-2.5 mr-0.5" />Venta</span>
                        ) : (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"><EyeIcon className="w-2.5 h-2.5 mr-0.5" />Anteo.</span>
                        )}
                      </td>
                      <td className="px-2 md:px-3 lg:px-4 py-2 md:py-3 whitespace-nowrap text-xs md:text-sm font-medium text-gray-900">
                        #{obtenerNumeroCaso(registro)}
                      </td>
                      <td className="px-2 md:px-3 lg:px-4 py-2 md:py-3 whitespace-nowrap">
                        <div className="text-xs md:text-sm font-medium text-gray-900 truncate">
                          {registro.cliente.nombre} {registro.cliente.apellido}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {registro.cliente.telefono}
                        </div>
                      </td>
                      <td className="px-2 md:px-3 lg:px-4 py-2 md:py-3 whitespace-nowrap text-xs md:text-sm text-gray-500 hidden md:table-cell">
                        {formatearFecha(registro.fecha_pedido)}
                      </td>
                      <td className="px-2 md:px-3 lg:px-4 py-2 md:py-3 whitespace-nowrap text-xs md:text-sm text-gray-500 hidden lg:table-cell">
                        {registro.tipo_anteojo}
                      </td>
                      <td className="px-2 md:px-3 lg:px-4 py-2 md:py-3">
                        {registro.tipo_origen === 'venta' ? (
                          <div className="text-xs space-y-1">
                            {registro.descripcion && (
                              <div className="text-gray-600 italic">
                                {registro.descripcion}
                              </div>
                            )}
                            {registro.nombre_armazon && (
                              <div className="text-gray-700 font-medium">
                                {registro.nombre_armazon}
                              </div>
                            )}
                            {!registro.nombre_armazon && registro.armazon_marca && (
                              <div className="text-gray-700 font-medium">
                                {registro.armazon_marca} {registro.armazon_modelo} {registro.armazon_color && `- ${registro.armazon_color}`}
                              </div>
                            )}
                            {!registro.descripcion && !registro.nombre_armazon && !registro.armazon_marca && (
                              <div className="text-gray-400">-</div>
                            )}
                          </div>
                        ) : (
                          <>
                            <div className="text-xs md:text-sm text-gray-900 flex items-center gap-0.5 truncate"><MagnifyingGlassIcon className="w-2.5 h-2.5 flex-shrink-0" />{registro.cristal}</div>
                            <div className="text-xs md:text-sm text-gray-500 flex items-center gap-0.5 truncate"><EyeIcon className="w-2.5 h-2.5 flex-shrink-0" />{registro.armazon}</div>
                          </>
                        )}
                      </td>
                      <td className="px-2 md:px-3 lg:px-4 py-2 md:py-3 whitespace-nowrap text-xs md:text-sm text-gray-500 hidden xl:table-cell">
                        {registro.tipo_pago || '-'}
                      </td>
                      <td className="px-2 md:px-3 lg:px-4 py-2 md:py-3 whitespace-nowrap text-xs md:text-sm font-medium text-gray-900">
                        {formatearMoneda(registro.precio_total)}
                      </td>
                      <td className="px-2 md:px-3 lg:px-4 py-2 md:py-3 whitespace-nowrap text-xs md:text-sm text-green-600 font-medium hidden sm:table-cell">
                        {formatearMoneda(registro.sena)}
                      </td>
                      <td className="px-2 md:px-3 lg:px-4 py-2 md:py-3 whitespace-nowrap">
                        <span className={`text-xs md:text-sm font-medium ${
                          registro.saldo > 0 ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {formatearMoneda(registro.saldo)}
                        </span>
                      </td>
                      <td className="px-2 md:px-3 lg:px-4 py-2 md:py-3 whitespace-nowrap text-xs md:text-sm hidden md:table-cell">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          registro.estado === 'Entregado' || registro.estado === 'Pagado' ? 'bg-green-100 text-green-800' :
                          registro.estado === 'Listo' ? 'bg-blue-100 text-blue-800' :
                          registro.estado === 'En proceso' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {registro.estado}
                        </span>
                      </td>
                      <td className="px-2 md:px-3 lg:px-4 py-2 md:py-3 whitespace-nowrap text-right text-xs md:text-sm font-medium">
                        {registro.tipo_origen === 'venta' && (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => { setVentaParaEditar(registro); setMostrarModalEditarVenta(true); }}
                              className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"
                              title="Editar venta"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => eliminarVentaConfirm(registro)}
                              className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded"
                              title="Eliminar venta"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
                </div>
              </div>
            </>
          )}
          {/* Totales de la tabla */}
          {registros.length > 0 && (
            <div className="bg-gray-50 px-2 md:px-4 lg:px-6 py-3 md:py-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4 text-xs md:text-sm">
                <div className="text-center">
                  <div className="font-semibold text-gray-900">
                    Total: {formatearMoneda(registros.reduce((sum, r) => sum + r.precio_total, 0))}
                  </div>
                </div>
                <div className="text-center">
                  <div className="font-semibold text-green-600">
                    Señas: {formatearMoneda(registros.reduce((sum, r) => sum + r.sena, 0))}
                  </div>
                </div>
                <div className="text-center">
                  <div className="font-semibold text-red-600">
                    Saldos: {formatearMoneda(registros.reduce((sum, r) => sum + r.saldo, 0))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {mostrarModalEgreso && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Registrar Egreso / Gasto</h3>
              <button
                type="button"
                onClick={cerrarModalEgreso}
                className="text-gray-500 hover:text-gray-700"
                disabled={procesandoAccion}
              >
                ✕
              </button>
            </div>

            <form onSubmit={guardarEgreso} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="egreso-fecha" className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
                  <input
                    id="egreso-fecha"
                    type="date"
                    value={formEgreso.fecha}
                    onChange={(e) => actualizarFormEgreso('fecha', e.target.value)}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-rose-500 focus:border-rose-500 sm:text-sm"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="egreso-importe" className="block text-sm font-medium text-gray-700 mb-1">Importe</label>
                  <input
                    id="egreso-importe"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formEgreso.importe}
                    onChange={(e) => actualizarFormEgreso('importe', e.target.value)}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-rose-500 focus:border-rose-500 sm:text-sm"
                    placeholder="Ej: 15000"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="egreso-tipo" className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                  <select
                    id="egreso-tipo"
                    value={formEgreso.tipo}
                    onChange={(e) => actualizarFormEgreso('tipo', e.target.value)}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-rose-500 focus:border-rose-500 sm:text-sm"
                    required
                  >
                    <option value="efectivo">Efectivo</option>
                    <option value="transferencia">Transferencia</option>
                    <option value="cheque">Cheque</option>
                    <option value="a_pagar_efet">A pagar (efect.)</option>
                    <option value="debito">Débito</option>
                    <option value="credito">Crédito</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="egreso-receptor" className="block text-sm font-medium text-gray-700 mb-1">Receptor</label>
                  <input
                    id="egreso-receptor"
                    type="text"
                    value={formEgreso.receptor}
                    onChange={(e) => actualizarFormEgreso('receptor', e.target.value)}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-rose-500 focus:border-rose-500 sm:text-sm"
                    placeholder="Proveedor o destinatario"
                    required
                  />
                </div>
              </div>

              {['cheque', 'transferencia', 'a_pagar_efet'].includes(formEgreso.tipo) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-md bg-amber-50 border border-amber-200">
                  <div>
                    <label htmlFor="egreso-fecha-cobro" className="block text-sm font-medium text-gray-700 mb-1">
                      {formEgreso.tipo === 'cheque' ? 'Fecha de cobro' : formEgreso.tipo === 'a_pagar_efet' ? 'Fecha de pago' : 'Fecha de acreditación'}
                    </label>
                    <input
                      id="egreso-fecha-cobro"
                      type="date"
                      value={formEgreso.fecha_cobro}
                      onChange={(e) => actualizarFormEgreso('fecha_cobro', e.target.value)}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-amber-500 focus:border-amber-500 sm:text-sm"
                      required
                    />
                  </div>

                  {formEgreso.tipo === 'cheque' ? (
                    <div>
                      <label htmlFor="egreso-nro-cheque" className="block text-sm font-medium text-gray-700 mb-1">Número de cheque</label>
                      <input
                        id="egreso-nro-cheque"
                        type="text"
                        value={formEgreso.nro_cheque}
                        onChange={(e) => actualizarFormEgreso('nro_cheque', e.target.value)}
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-amber-500 focus:border-amber-500 sm:text-sm"
                        placeholder="Ej: 00012345"
                        required
                      />
                    </div>
                  ) : (
                    <div className="flex items-end text-sm text-amber-800">
                      {formEgreso.tipo === 'a_pagar_efet'
                        ? 'El pago en efectivo se mostrará en el calendario según esta fecha.'
                        : 'La transferencia se mostrará en el calendario según esta fecha.'}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={cerrarModalEgreso}
                  disabled={procesandoAccion}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={procesandoAccion}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50"
                >
                  {procesandoAccion ? 'Guardando...' : 'Guardar egreso'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Calendario de Pagos Modal */}
      <CalendarioPagos 
        isOpen={mostrarCalendarioPagos} 
        onClose={() => setMostrarCalendarioPagos(false)} 
      />

      {/* Gestión de Egresos Modal */}
      {mostrarGestionEgresos && (
        <GestionEgresos onClose={() => setMostrarGestionEgresos(false)} />
      )}

      {/* Modal de Edición de Ventas */}
      {mostrarModalEditarVenta && ventaParaEditar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full max-h-96 overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Editar Venta</h3>
                <button
                  onClick={() => { setMostrarModalEditarVenta(false); setVentaParaEditar(null); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={guardarEdicionVenta} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tipo de Venta</label>
                  <input
                    type="text"
                    value={ventaParaEditar.tipo_venta_nombre || ''}
                    readOnly
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Cliente</label>
                  <input
                    type="text"
                    value={`${ventaParaEditar.cliente?.nombre} ${ventaParaEditar.cliente?.apellido}`}
                    readOnly
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Monto</label>
                  <input
                    type="number"
                    value={ventaParaEditar.precio_total || ''}
                    onChange={(e) => setVentaParaEditar({ ...ventaParaEditar, precio_total: Number.parseFloat(e.target.value) || 0 })}
                    step="0.01"
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Forma de Pago</label>
                  <select
                    value={ventaParaEditar.tipo_pago || 'efectivo'}
                    onChange={(e) => setVentaParaEditar({ ...ventaParaEditar, tipo_pago: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="efectivo">Efectivo</option>
                    <option value="tarjeta_credito">Tarjeta de Crédito</option>
                    <option value="tarjeta_debito">Tarjeta de Débito</option>
                    <option value="transferencia">Transferencia</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Descripción</label>
                  <textarea
                    value={ventaParaEditar.observaciones || ''}
                    onChange={(e) => setVentaParaEditar({ ...ventaParaEditar, observaciones: e.target.value })}
                    rows="3"
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={procesandoVenta}
                    className="flex-1 bg-green-600 text-white py-2 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {procesandoVenta ? 'Guardando...' : 'Guardar cambios'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMostrarModalEditarVenta(false); setVentaParaEditar(null); }}
                    disabled={procesandoVenta}
                    className="flex-1 bg-gray-300 text-gray-800 py-2 rounded-md hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Finanzas;
